import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { Drawer } from '@mui/material';
import VehicleDetail from 'src/components/VehicleTrackingCreate/Inward/VehicleDetails';
import { ReactComponent as DetailLinkIcon } from 'src/Images/icons/common/detailLinkIcon.svg';
import {
  getVehicleTrackingWorkflows,
  getPlants,
  getTenantPermissions,
} from 'src/redux/Actions/VehicleTracking';
import {
  getVehicleDropdownOptions,
  getTransporterDropdownOptions,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { ACTIONS } from 'src/utils/roles/actions';
import { usePermissions } from 'src/utils/hooks/usePermissions';
import 'src/style/createVehicleTracking.css';

function OutwardVehicleTrackingHeaderSection({
  formData,
  handleOnChange,
  setFormData,
  transporterSearchTerm,
  setTransporterSearchTerm,
  vehicleSearchTerm,
  setVehicleSearchTerm,
  errorField,
}) {
  const dispatch = useDispatch();
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const authReducer = useSelector((state) => state.AuthReducer);

  const {
    hasPermission,
    positionPermissions,
    teamPermissions,
    isPermissionsLoaded,
  } = usePermissions();

  const noPermissionsAssigned =
    isPermissionsLoaded &&
    (positionPermissions?.length === 0 || !positionPermissions) &&
    (teamPermissions?.length === 0 || !teamPermissions);

  const hasInwardPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.INWARD.CREATE,
  );
  const hasOutwardPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CREATE,
  );

  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );

  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const tenantId = authReducer?.userData?.tenant?.id;

  const availableWorkflows = vehicleTrackingDataReducer.workflows;
  const tenantPermission = vehicleTrackingDataReducer?.tenantPermission || [];
  const [isVahanVerificationDrawerOpen, setIsVahanVerificationDrawerOpen] =
    useState(false);
  const { plantsData } = vehicleTrackingDataReducer;

  const { transporterDropdownOptions, vehicleDropdownOptions } =
    VehicleTrackingOutwardReducer;
  const isCreatingNewTransporter =
    formData?.transporterId === null && formData?.transporterName;
  const isCreatingNewVehicle =
    formData?.selectedVehicleOption?.value === null && formData?.vehicleNumber;
  const isTransporterInvalid =
    !formData?.transporterId && !formData?.transporterName;
  const isVehicleFormFieldEmpty =
    !formData?.selectedVehicleOption?.value && !formData?.vehicleNumber;

  useEffect(() => {
    const vehicleId =
      formData?.selectedVehicleOption?.value && formData?.vehicleNumber
        ? formData.selectedVehicleOption.value
        : null;

    dispatch(
      getTransporterDropdownOptions(
        tenantOrganisationId,
        transporterSearchTerm,
        vehicleId,
      ),
    );
  }, [
    dispatch,
    tenantOrganisationId,
    transporterSearchTerm,
    formData?.selectedVehicleOption,
  ]);

  const fetchVehicleDropdownOptions = useCallback(() => {
    const transporterId = formData?.transporterId || null;

    dispatch(
      getVehicleDropdownOptions(
        tenantOrganisationId,
        vehicleSearchTerm,
        transporterId,
      ),
    );
  }, [
    dispatch,
    tenantOrganisationId,
    vehicleSearchTerm,
    formData?.transporterId,
  ]);

  useEffect(() => {
    fetchVehicleDropdownOptions();
  }, [fetchVehicleDropdownOptions]);

  useEffect(() => {
    const shouldAutoSelectVehicle =
      !vehicleSearchTerm &&
      formData?.transporterId &&
      formData?.transporterName;

    if (
      shouldAutoSelectVehicle &&
      !formData?.selectedVehicleOption &&
      vehicleDropdownOptions?.length === 1
    ) {
      const [vehicleOption] = vehicleDropdownOptions;

      setFormData((prev) => ({
        ...prev,
        selectedVehicleOption: {
          value: vehicleOption.id,
          label: vehicleOption.vehicle_number,
        },
        vehicleNumber: vehicleOption.vehicle_number,
      }));
    }
  }, [vehicleDropdownOptions, vehicleSearchTerm, formData?.transporterName]);
  useEffect(() => {
    const shouldAutoSelectTransporter =
      !transporterSearchTerm &&
      formData?.selectedVehicleOption?.value &&
      formData?.vehicleNumber;

    if (
      shouldAutoSelectTransporter &&
      !formData?.transporterId &&
      !isCreatingNewTransporter &&
      transporterDropdownOptions?.length === 1
    ) {
      const [transporterOption] = transporterDropdownOptions;
      setFormData((prev) => ({
        ...prev,
        transporterId: transporterOption.id,
        transporterName: transporterOption.name,
      }));
    }
  }, [
    transporterDropdownOptions,
    transporterSearchTerm,
    formData?.vehicleNumber,
  ]);

  useEffect(() => {
    if (tenantOrganisationId) {
      dispatch(getVehicleTrackingWorkflows(tenantOrganisationId, '2')); // hardcoded to outward
      dispatch(getPlants(tenantOrganisationId));
    }
  }, [dispatch, tenantOrganisationId]);

  useEffect(() => {
    if (tenantId) {
      dispatch(getTenantPermissions(tenantId));
    }
  }, [dispatch, tenantId]);

  useEffect(() => {
    if (!availableWorkflows?.length) return;
    const selectedWorkflowExists = availableWorkflows.some(
      (workflow) => workflow.id === formData?.workflowId,
    );
    if (!selectedWorkflowExists) {
      setFormData((prev) => ({
        ...prev,
        workflowId: availableWorkflows[0].id,
      }));
    }
  }, [availableWorkflows]);

  // MAKE IT SIMPLE USING IF-ELSE BLOCK
  let vehicleNumberDataOption = [],
    businessPartnerDataOption = [];
  if (vehicleDropdownOptions && vehicleDropdownOptions.length > 0) {
    vehicleNumberDataOption = vehicleDropdownOptions.map((vehicleObj) => ({
      value: vehicleObj.id,
      label: vehicleObj.vehicle_number,
    }));
  } else if (
    vehicleSearchTerm &&
    vehicleDropdownOptions &&
    vehicleDropdownOptions.length === 0
  ) {
    vehicleNumberDataOption.push({
      value: `create-${vehicleSearchTerm}`,
      label: (
        <div className="d-flex justify-content-between align-items-center text-blue">
          <span>Create vehicle</span>
          <span>+</span>
        </div>
      ),
      isCreateOption: true,
    });
  }

  // MAKE IT SIMPLE USING IF-ELSE BLOCK

  if (transporterSearchTerm && transporterDropdownOptions?.length === 0) {
    businessPartnerDataOption.push({
      value: `create-${transporterSearchTerm}`,
      label: (
        <div className="d-flex justify-content-between align-items-center text-blue">
          <span>Create transporter</span>
          <span>+</span>
        </div>
      ),
      isCreateOption: true,
    });
  } else if (
    transporterDropdownOptions &&
    transporterDropdownOptions.length > 0
  ) {
    businessPartnerDataOption = transporterDropdownOptions.map(
      (transporterObj) => ({
        value: transporterObj.id,
        label: transporterObj.name,
      }),
    );
  }

  const handleVehicleChange = (selectedOption) => {
    const newVehicleNumber = vehicleSearchTerm;
    setVehicleSearchTerm('');

    if (!selectedOption) {
      setFormData((prev) => ({
        ...prev,
        selectedVehicleOption: null,
        vehicleNumber: '',
      }));
      return;
    }

    if (selectedOption.isCreateOption) {
      setFormData((prev) => ({
        ...prev,
        selectedVehicleOption: {
          value: null,
          label: newVehicleNumber,
        },
        vehicleNumber: newVehicleNumber,
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      selectedVehicleOption: {
        value: selectedOption.value,
        label: selectedOption.label,
      },
      vehicleNumber: selectedOption.label,
      transporterId: prev.transporterId,
      transporterName: prev.transporterName,
    }));
  };

  const plantDataOption =
    plantsData?.map((plantObj) => ({
      value: plantObj.id,
      label: plantObj.name,
    })) || [];

  const workflowDataOption =
    availableWorkflows?.map((availableWorkflow) => ({
      value: availableWorkflow.id,
      label: availableWorkflow.name,
    })) || [];

  const transporterValue = (() => {
    if (isCreatingNewTransporter) {
      return {
        label: formData.transporterName,
        value: null,
      };
    }

    const existingOption = businessPartnerDataOption.find(
      (option) => option.value === formData?.transporterId,
    );

    return existingOption || '';
  })();

  const vehicleValue = (() => {
    if (!formData?.selectedVehicleOption) return '';

    if (isCreatingNewVehicle) {
      return {
        label: formData.vehicleNumber,
        value: null,
      };
    }

    return (
      vehicleNumberDataOption.find(
        (option) => option.value === formData.selectedVehicleOption.value,
      ) || formData.selectedVehicleOption
    );
  })();

  const selectedVehicleNumber = formData?.vehicleNumber || '';
  const displayVahanDrawerButton =
    tenantPermission?.isVahanIntegrationEnabled && selectedVehicleNumber;

  return (
    <>
      <div className="container-card-shadow border-radius-4 ">
        <div className="w-100 card-bg div-flex-row py-2 px-3 cg-10">
          <input
            type="checkbox"
            name="enableLoadSequence"
            checked={formData?.enableLoadSequence || false}
            onChange={handleOnChange}
          />
          <span className="text-sm">
            To process your load in sequence click on the checkbox.
          </span>
        </div>
        <div className="container-padding-10 container-padding-5">
          <Row>
            <Col xxl={3} xl={3} lg={4} md={4}>
              <div className="div-flex-column">
                <div className="div-flex-row">
                  <span className="text-sm text-bold">
                    Transporter Name
                    <span className="text-red">&nbsp;*</span>
                  </span>
                </div>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    name="transporterId"
                    isMulti={false}
                    className={`margin-top-5 ${errorField && isTransporterInvalid ? 'react-select-input-error' : 'multi-select-input-section'}`}
                    onChange={(selectedOption) => {
                      const newTransporterId = selectedOption?.isCreateOption
                        ? null
                        : selectedOption?.value || null;
                      const newTransporterName = selectedOption?.isCreateOption
                        ? transporterSearchTerm
                        : selectedOption?.label || null;

                      setTransporterSearchTerm('');
                      if (!isVehicleFormFieldEmpty) {
                        setFormData((prev) => ({
                          ...prev,
                          transporterId: newTransporterId,
                          transporterName: newTransporterName,
                        }));
                        return;
                      }

                      handleOnChange({
                        target: {
                          name: 'transporterId',
                          value: newTransporterId,
                          transporterName: newTransporterName,
                          isCreateOption: selectedOption?.isCreateOption,
                        },
                      });
                    }}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        setTransporterSearchTerm(inputValue);
                      }
                    }}
                    options={businessPartnerDataOption}
                    classNamePrefix="react-select"
                    placeholder="Search Transporter Name"
                    value={transporterValue}
                    onBlur={() => setTransporterSearchTerm('')}
                  />
                  {!formData?.transporterName && (
                    <span className="form-input-section-icon">
                      <Search className="icon-size-sm" color="#9F9F9F" />
                    </span>
                  )}
                </div>
                {errorField && isTransporterInvalid && (
                  <span className="text-sm text-red">
                    This field is required
                  </span>
                )}
              </div>
            </Col>
            <Col xxl={3} xl={3} lg={4} md={4}>
              <div className="div-flex-column">
                <div className="div-flex-row">
                  <span className="text-sm text-bold">
                    Vehicle Number
                    <span className="text-red">&nbsp;*</span>
                  </span>
                </div>
                <div className="input-wrapper vehicle-number-input-wrapper">
                  <CustomMultiSelect
                    name="vehicleId"
                    isMulti={false}
                    className={`margin-top-5 ${errorField && isVehicleFormFieldEmpty ? 'react-select-input-error' : 'multi-select-input-section'}`}
                    onChange={handleVehicleChange}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        setVehicleSearchTerm(inputValue.toUpperCase());
                      }
                    }}
                    onMenuOpen={fetchVehicleDropdownOptions}
                    options={vehicleNumberDataOption}
                    classNamePrefix="react-select"
                    placeholder="Search Vehicle Number"
                    value={vehicleValue}
                    onBlur={() => setVehicleSearchTerm('')}
                  />
                  {!displayVahanDrawerButton && !formData?.vehicleNumber && (
                    <span className="form-input-section-icon">
                      <Search className="icon-size-sm" color="#9F9F9F" />
                    </span>
                  )}
                  {displayVahanDrawerButton && (
                    <button
                      type="button"
                      className="vehicle-number-open-btn"
                      onClick={() => setIsVahanVerificationDrawerOpen(true)}
                      aria-label="Open vehicle details"
                    >
                      <DetailLinkIcon className="icon-size-md" />
                    </button>
                  )}
                </div>
                {errorField && isVehicleFormFieldEmpty && (
                  <span className="text-sm text-red">
                    This field is required
                  </span>
                )}
              </div>
            </Col>
            <Col xxl={3} xl={3} lg={4} md={4}>
              <div className="div-flex-column">
                <span className="text-sm text-bold">Plant</span>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    name="plant"
                    isMulti={false}
                    className="multi-select-input-section margin-top-5"
                    onChange={(selectedOption) => {
                      handleOnChange({
                        target: {
                          name: 'plant',
                          value: selectedOption?.value || null,
                        },
                      });
                    }}
                    options={plantDataOption}
                    classNamePrefix="react-select"
                    placeholder="Search Plant"
                    value={
                      plantDataOption.find(
                        (option) => option.value === formData?.plant,
                      ) || ''
                    }
                  />
                  <span className="form-input-section-icon">
                    <Search className="icon-size-sm" color="#9F9F9F" />
                  </span>
                </div>
              </div>
            </Col>
            <Col xxl={3} xl={3} lg={4} md={4}>
              <div className="div-flex-column">
                <div className="div-flex-row cg-5">
                  <span className="text-sm text-bold">Workflow</span>
                </div>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    name="workflowId"
                    isMulti={false}
                    className="margin-top-5 multi-select-input-section"
                    onChange={(selectedOption) => {
                      handleOnChange({
                        target: {
                          name: 'workflowId',
                          value: selectedOption?.value || null,
                        },
                      });
                    }}
                    options={workflowDataOption}
                    classNamePrefix="react-select"
                    placeholder="Select Workflow"
                    value={workflowDataOption.find(
                      (option) => option.value === formData?.workflowId,
                    )}
                  />
                  {/* for handling form error */}
                  <span className="form-input-section-icon">
                    <Search className="icon-size-sm" color="#9F9F9F" />
                  </span>
                </div>
              </div>
            </Col>
            <Col xxl={3} xl={3} lg={4} md={4}>
              <div className="div-flex-column">
                <div className="div-flex-row cg-5">
                  <span className="text-sm text-bold">Direction</span>
                </div>
                <div className="div-flex-row cg-20 margin-top-5">
                  <div className="div-flex-row cg-5">
                    <input
                      type="radio"
                      name="vehicleTrackingType"
                      value="1"
                      checked={formData?.vehicleTrackingType === '1'}
                      onChange={handleOnChange}
                      disabled={!noPermissionsAssigned && !hasInwardPermission}
                    />
                    <span className="text-sm">Inward</span>
                  </div>
                  <div className="div-flex-row cg-5">
                    <input
                      type="radio"
                      name="vehicleTrackingType"
                      value="2"
                      checked={formData?.vehicleTrackingType === '2'}
                      onChange={handleOnChange}
                      disabled={!noPermissionsAssigned && !hasOutwardPermission}
                    />
                    <span className="text-sm">Outward</span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <Drawer
        anchor="right"
        open={isVahanVerificationDrawerOpen}
        onClose={() => {
          setIsVahanVerificationDrawerOpen(false);
        }}
        ModalProps={{ keepMounted: false }}
        sx={{ zIndex: 1 }}
        PaperProps={{
          sx: {
            width: {
              xs: '100%',
              sm: '100%',
              md: '50%',
            },
          },
        }}
      >
        <VehicleDetail
          vehicleNumber={selectedVehicleNumber}
          onClose={() => setIsVahanVerificationDrawerOpen(false)}
        />
      </Drawer>
    </>
  );
}

export default OutwardVehicleTrackingHeaderSection;
